import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
    const { orgCode } = useParams();
    const { tenant } = useTenant();
    
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [resetLink, setResetLink] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`${API}/auth/forgot-password`, {
                email,
                organization_code: orgCode
            });
            
            setSubmitted(true);
            // Store reset link for display (since email is mocked)
            if (response.data.reset_link) {
                setResetLink(response.data.reset_link);
            }
            toast.success('Anfrage gesendet');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Anfrage fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <AuthLayout>
                <Card className="border-0 shadow-lg">
                    <CardContent className="pt-8 pb-6">
                        <div className="text-center">
                            <div 
                                className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
                                style={{ backgroundColor: `${tenant?.primary_color}15` }}
                            >
                                <CheckCircle2 size={32} style={{ color: tenant?.primary_color }} />
                            </div>
                            <h2 className="text-xl font-bold text-slate-900 mb-2">
                                E-Mail gesendet
                            </h2>
                            <p className="text-slate-600 mb-6">
                                Falls ein Konto mit dieser E-Mail existiert, haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet.
                            </p>
                            
                            {/* Show reset link since email is mocked */}
                            {resetLink && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                                    <p className="text-sm text-amber-800 font-medium mb-2">
                                        Test-Modus: Link zum Zurücksetzen
                                    </p>
                                    <p className="text-xs text-amber-700 break-all">
                                        <a 
                                            href={resetLink} 
                                            className="hover:underline"
                                            style={{ color: tenant?.primary_color }}
                                        >
                                            {resetLink}
                                        </a>
                                    </p>
                                </div>
                            )}
                            
                            <Link to={`/o/${orgCode}/login`}>
                                <Button variant="outline" className="w-full">
                                    <ArrowLeft size={16} className="mr-2" />
                                    Zurück zur Anmeldung
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold">Passwort vergessen?</CardTitle>
                    <CardDescription>
                        Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">E-Mail-Adresse</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="ihre.email@beispiel.de"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="forgot-email-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full"
                            style={{ backgroundColor: tenant?.primary_color }}
                            disabled={loading}
                            data-testid="forgot-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Wird gesendet...
                                </>
                            ) : (
                                <>
                                    <Mail size={16} className="mr-2" />
                                    Link anfordern
                                </>
                            )}
                        </Button>
                    </form>
                    
                    <div className="mt-6 text-center">
                        <Link 
                            to={`/o/${orgCode}/login`}
                            className="text-sm text-slate-600 hover:underline inline-flex items-center"
                        >
                            <ArrowLeft size={14} className="mr-1" />
                            Zurück zur Anmeldung
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </AuthLayout>
    );
};
